import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { ErrorBox } from "../../components/ErrorBox";
import { PageHeader } from "../../components/PageHeader";
import { TableScroll } from "../lib/ui";
import { type Patch, useUrlState } from "../lib/url";
import { BoardTable } from "./BoardTable";
import {
  type BoardParams, QA_CATEGORIES, RENDER_STATUSES, boardApiQuery, boardMdParams, nextSort, parseBoardParams, toggleOpen,
} from "./params";

const GROUP_TITLES: Record<string, string> = { videos: "Videoclips", songs: "Songs", renders: "Renders" };

/** Rows fetched per "Show more" (offset paging on GET /api/board/{group}). */
export const BOARD_PAGE_SIZE = 200;

type BoardPageT = Schemas["BoardPage"];

/** The loaded pages as one page (the first page's header and total, every page's items, duplicates dropped). */
export function mergeBoardPages(pages: BoardPageT[]): BoardPageT | undefined {
  const first = pages[0];
  if (!first) return undefined;
  const seen = new Set<number>();
  const items = pages.flatMap((pg) => pg.items).filter((it) => (seen.has(it.id) ? false : (seen.add(it.id), true)));
  return { ...first, total: pages.at(-1)?.total ?? first.total, items };
}

export function useArtists() {
  return useQuery({
    queryKey: qk.list("artists"),
    queryFn: () => unwrap(api.GET("/api/artists", { params: { query: { include_inactive: true } } })),
    retry: false,
    staleTime: 60_000,
  });
}

function ArtistFilter({ value, onChange }: { value?: number; onChange: (id: number | undefined) => void }) {
  const artists = useArtists();
  const [typed, setTyped] = useState(value !== undefined ? String(value) : "");
  useEffect(() => setTyped(value !== undefined ? String(value) : ""), [value]);
  if (artists.isError) {
    // WS1 artists route unavailable: still filterable by id (the URL is the source of truth anyway)
    return (
      <span className="ws7-inline">
        <label className="ws7-field">
          Artist id
          <input type="number" min={1} value={typed} data-testid="filter-artist-id" onChange={(e) => setTyped(e.target.value)} />
        </label>
        <button type="button" data-testid="filter-artist-apply" onClick={() => onChange(typed ? Number(typed) : undefined)}>
          Apply artist
        </button>
      </span>
    );
  }
  const list = artists.data ?? [];
  const known = value === undefined || list.some((a) => a.id === value);
  return (
    <label className="ws7-field">
      Artist
      <select data-testid="filter-artist" value={value ?? ""} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}>
        <option value="">All artists</option>
        {list.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        {!known && <option value={value}>Artist #{value}</option>}
      </select>
    </label>
  );
}

function Filters({ group, p, set }: { group: string; p: BoardParams; set: (patch: Patch, msg: string) => void }) {
  const [q, setQ] = useState(p.q ?? "");
  useEffect(() => setQ(p.q ?? ""), [p.q]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    set({ q: q.trim() || undefined }, q.trim() ? `Search: “${q.trim()}”` : "Search cleared");
  };
  return (
    <form className="ws7-toolbar" role="search" aria-label="Board filters" onSubmit={submit}>
      <label className="ws7-field">
        Search
        <input type="search" value={q} data-testid="filter-q" onChange={(e) => setQ(e.target.value)} />
      </label>
      <button type="submit" data-testid="filter-search">Search</button>
      <ArtistFilter value={p.artist_id} onChange={(id) => set({ artist_id: id }, id ? `Filter: artist #${id}` : "Filter: all artists")} />
      {group !== "renders" && (
        <label className="ws7-check">
          <input type="checkbox" checked={p.show_all} data-testid="filter-show-all"
            onChange={(e) => set({ show_all: e.target.checked }, e.target.checked ? "Showing all items" : "Showing the per-artist selection")} />
          {group === "videos" ? "Show all (not only the newest and most-viewed per artist)" : "Show all (not only the newest per artist)"}
        </label>
      )}
      {group === "renders" && (
        <>
          <label className="ws7-field">
            Status
            <select value={p.status ?? ""} data-testid="filter-status" onChange={(e) => set({ status: e.target.value || undefined }, `Filter: status ${e.target.value || "any"}`)}>
              <option value="">Any status</option>
              {RENDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="ws7-field">
            QA category
            <select value={p.qa_category ?? ""} data-testid="filter-qa" onChange={(e) => set({ qa_category: e.target.value || undefined }, `Filter: QA ${e.target.value || "any"}`)}>
              <option value="">Any QA category</option>
              {QA_CATEGORIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="ws7-check">
            <input type="checkbox" checked={p.unreviewed} data-testid="filter-unreviewed"
              onChange={(e) => set({ unreviewed: e.target.checked }, e.target.checked ? "Filter: unreviewed only" : "Filter: reviewed and unreviewed")} />
            Unreviewed only
          </label>
        </>
      )}
      <button type="button" data-testid="filter-clear"
        onClick={() => set({ q: undefined, artist_id: undefined, show_all: undefined, status: undefined, qa_category: undefined, unreviewed: undefined, sort: undefined, desc: undefined }, "Filters cleared")}>
        Clear filters
      </button>
    </form>
  );
}

export function BoardView({ group }: { group: string }) {
  const announce = useAnnounce();
  const { sp, set } = useUrlState();
  const p = parseBoardParams(sp);
  const apiQuery = boardApiQuery(group, p);
  const board = useInfiniteQuery({
    queryKey: [...qk.board(group, apiQuery), "pages"],
    queryFn: ({ pageParam }) => unwrap(api.GET("/api/board/{group}", {
      params: { path: { group }, query: { ...apiQuery, limit: BOARD_PAGE_SIZE, offset: pageParam } },
    })),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, pg) => n + pg.items.length, 0);
      return last.items.length > 0 && loaded < last.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = board.data ? mergeBoardPages(board.data.pages) : undefined;
  const presence = useQuery({
    queryKey: qk.presence,
    queryFn: () => unwrap(api.GET("/api/presence")),
    refetchInterval: 30_000,
  });
  const title = data?.title ?? GROUP_TITLES[group] ?? group;
  const change = (patch: Patch, msg: string) => {
    set(patch);
    announce(msg);
  };
  return (
    <div className="ws7-page" data-testid={`board-${group}`}>
      <PageHeader title={`Board: ${title}`} md={{ page: "board", params: boardMdParams(group, p) }} />
      <Filters group={group} p={p} set={change} />
      {board.isError && <ErrorBox error={board.error} />}
      {board.isPending && <p className="ws7-muted" data-testid="loading">Loading {title}…</p>}
      {data && (
        <section className={`ws7-group ws7-group-${group}`} aria-labelledby={`group-${group}-h`}>
          <div className="ws7-group-head">
            <button type="button" data-testid="group-collapse" aria-expanded={!p.collapsed} aria-controls={`group-${group}-body`}
              onClick={() => change({ collapsed: !p.collapsed }, p.collapsed ? `Expanded ${title}` : `Collapsed ${title}`)}>
              {p.collapsed ? "Expand" : "Collapse"}
            </button>
            <h2 id={`group-${group}-h`}>{data.title}</h2>
            <span className="ws7-count" data-testid="board-count">{data.total} {data.total === 1 ? "item" : "items"}</span>
          </div>
          {data.help && <p className="ws7-muted ws7-board-help" data-testid="board-help">{data.help}</p>}
          {!p.collapsed && (
            <div id={`group-${group}-body`}>
              {data.items.length === 0 ? (
                <p className="ws7-muted" style={{ padding: "8px 12px" }} data-testid="empty">No items match these filters.</p>
              ) : (
                <>
                <TableScroll label={`${data.title} table`} className="ws7-scroll ws7-board-scroll">
                    <BoardTable
                      page={data}
                      group={group}
                      label={data.title}
                      presence={presence.data ?? []}
                      open={p.open}
                      sort={{ sort: p.sort, desc: p.desc }}
                      onSort={(key) => {
                        const s = nextSort(p, key);
                        const col = key === "title" ? "Title" : (data.columns.find((c) => c.key === key)?.title ?? key);
                        change({ sort: s.sort, desc: s.desc ? undefined : "0" }, `Sorted by ${col}, ${s.desc ? "descending" : "ascending"}`);
                      }}
                      onToggle={(it) => {
                        const next = toggleOpen(p.open, it.id);
                        change({ open: next.length ? next.join(",") : undefined },
                          `${next.includes(it.id) ? "Showing" : "Hid"} subitems of ${it.title}`);
                      }}
                    />
                </TableScroll>
                <div className="ws7-row ws7-board-more">
                  <span className="ws7-muted" data-testid="board-showing">Showing {data.items.length} of {data.total}</span>
                  {board.hasNextPage && (
                    <button type="button" data-testid="board-more" disabled={board.isFetchingNextPage}
                      aria-label={`Show more ${data.title} (next ${Math.min(BOARD_PAGE_SIZE, data.total - data.items.length)})`}
                      onClick={async () => {
                        const r = await board.fetchNextPage();
                        const merged = r.data ? mergeBoardPages(r.data.pages) : undefined;
                        if (merged) announce(`Showing ${merged.items.length} of ${merged.total} ${data.title}`);
                      }}>
                      {board.isFetchingNextPage ? "Loading…" : "Show more"}
                    </button>
                  )}
                </div>
                </>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

